import React, { useEffect, useReducer } from "react";
import ReactModal from "react-modal";
import { DocumentSignatureKindText } from "../../../TextTables";

type SignatureDialogState = "what" | "method" | "kind" | "display" | "witness" | "close" | "selection";

type SignatureDialogAction =
    {
        type: "reset",
        mode: "document" | "field"
    } | {
        type: "close",
        reset: boolean
    } | {
        type: "what",
        mode: "document" | "field"
    } | {
        type: "method",
        method: "electronic" | "pad"
    } | {
        type: "kind",
        kind: DocumentSignatureKindText | "witness",
        kindTitle?: string,
        field?: string
    };

export interface ISignatureOption {
    fieldId: string;
    kind: DocumentSignatureKindText;
    title: string;
}

export interface ISignatureDialogReturnValues {
    what: "document" | "field";
    method: "electronic" | "pad";
    kind?: DocumentSignatureKindText;
    mime?: string;
    data?: string;
    field?: string;
}

interface ISignatureDialogProps {
    isOpen: boolean;
    onRequestClose: (data?: ISignatureDialogReturnValues) => void;
    allowDocumentSign: boolean;
    mode: "document" | "field";
    availableSignatures?: ISignatureOption[];
}

interface ISignatureDialogState {
    state: SignatureDialogState;
    mode: "document" | "field";
    method: "electronic" | "pad" | null;
    kind: DocumentSignatureKindText | "witness" | null;
    kindTitle: string;
    field: string | null;
}

const whatText = {
    "document": "Whole document",
    "field": "Specific field",
};

const methodText = {
    "electronic": "electronically",
    "pad": "using signature pad",
};

const signatureTypeText: { [type: string]: string } = {
    "author": "an author",
    "witness": "a witness",
    "participant": "a participant",
    "verification": "verification",
    "transcriptionist": "the transcriptionist",
    "event_witness": "an event witness",
    "identity_witness": "an identity witness",
    "consent_witness": "a consent witness",
}

function renderDialogState(props: ISignatureDialogProps, state: ISignatureDialogState, dispatch: React.Dispatch<SignatureDialogAction>) {
    switch (state.state) {
        case "what":
            return <>
                <p>
                    What do you want to sign?
                </p>
                <div className="button-card-list">
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "what", mode: "document" })} disabled={!props.allowDocumentSign}>
                        <i className="icon-file-text-o" />
                        <div className="title">Entire document</div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "what", mode: "field" })} disabled={true}>
                        <i className="icon-th-list" />
                        <div className="title">Document fields</div>
                    </button>
                </div>
            </>;
        case "method":
            return <>
                <p>
                    How do you want to sign?
                </p>
                <div className="button-card-list">
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "method", method: "electronic" })}>
                        <i className="icon-magic" />
                        <div className="title">Electronically</div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "method", method: "pad" })} disabled={true}>
                        <i className="icon-pencil" />
                        <div className="title">Signature pad</div>
                    </button>
                </div>
            </>;
        case "kind":
            return <>
                <p>
                    What are you signing as?
                </p>
                <div className="button-card-list button-card-list-vertical">
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "author" })}>
                        <i className="icon-user" />
                        <div>
                            <div className="title">Author</div>
                            <div className="description">You are one of the authors of this document</div>
                        </div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "witness" })}>
                        <i className="icon-eye" />
                        <div>
                            <div className="title">Witness</div>
                            <div className="description">You are a witness related to this document</div>
                        </div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "participant" })}>
                        <i className="icon-user-plus" />
                        <div>
                            <div className="title">Participant</div>
                            <div className="description">You are a participant related to this document</div>
                        </div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "verification" })}>
                        <i className="icon-check" />
                        <div>
                            <div className="title">Verification</div>
                            <div className="description">You are verifying the accuracy of the contents of this document</div>
                        </div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "transcriptionist" })}>
                        <i className="icon-keyboard-o" />
                        <div>
                            <div className="title">Transcriptionist</div>
                            <div className="description">You entered this document into the system</div>
                        </div>
                    </button>
                </div>
            </>;
        case "witness":
            return <>
                <p>
                    What kind of witness?
                </p>
                <div className="button-card-list button-card-list-vertical">
                    {/*<button type="button" className="btn-card">
                        <i className="icon-eye" />
                        <div>
                            <div className="title">Signature witness</div>
                            <div className="description">You witnessed the signing of this document</div>
                        </div>
                    </button>*/}
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "event_witness" })}>
                        <i className="icon-calendar" />
                        <div>
                            <div className="title">Event witness</div>
                            <div className="description">You witnessed an event described in this document</div>
                        </div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "identity_witness" })}>
                        <i className="icon-id-card-o" />
                        <div>
                            <div className="title">Identity witness</div>
                            <div className="description">You witnessed the identity of someone in this document</div>
                        </div>
                    </button>
                    <button type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: "consent_witness" })}>
                        <i className="icon-pencil" />
                        <div>
                            <div className="title">Consent witness</div>
                            <div className="description">You witnessed the consent of someone in this document</div>
                        </div>
                    </button>
                </div>
            </>;
        case "display":
            return <>
                <p>You are about to sign this document:</p>
                <p>As <b>{state.kindTitle || (state.kind && signatureTypeText[state.kind])}</b> <span>{state.method && methodText[state.method]}</span>
                </p>
                <div className="action-panel-dialog">
                    <span className="spring" />
                    <button type="button" className="button green text-with-icon" onClick={() => dispatch({ type: "close", reset: false })}>
                        <i className="icon-pencil" /><span>Sign</span>
                    </button>
                    <button type="button" className="button text-with-icon" onClick={() => props.onRequestClose()}>
                        <i className="icon-times" /><span>Cancel</span>
                    </button>
                </div>
            </>;
        case "selection":
            return <>
                <p>Which box are you signing?</p>
                <div className="button-card-list button-card-list-vertical">
                    {props.availableSignatures!.map(a =>
                        <button key={a.fieldId} type="button" className="btn-card" onClick={() => dispatch({ type: "kind", kind: a.kind, kindTitle: a.title, field: a.fieldId })}>
                            <div>
                                <div className="title">{a.title}</div>
                                <div className="description">{a.title} signature</div>
                            </div>
                        </button>
                    )}
                </div>
            </>;
        default:
            return "Signing...";
    }
}

function reducer(state: ISignatureDialogState, action: SignatureDialogAction, props: ISignatureDialogProps): ISignatureDialogState {
    switch (action.type) {
        case "reset":
            if (props.availableSignatures && props.availableSignatures.length > 1) {
                // Need to display list of signature types
                return { state: "selection", mode: action.mode, method: null, kind: null, kindTitle: "", field: null };
            }
            else if (props.availableSignatures?.length !== 0) {
                // Only a single kind, use it
                const signature = props.availableSignatures![0];
                return { state: "method", mode: action.mode, method: null, kind: signature.kind, kindTitle: signature.title, field: signature.fieldId };
            }

            return { state: "kind", mode: action.mode, method: null, kind: null, kindTitle: "", field: null };
        case "what":
            return { ...state, state: "method", mode: action.mode };
        case "method":
            //if (state.mode === "field") {
                return { ...state, state: "display", method: action.method };
            //}
            //else {
            //    return { ...state, state: "kind", method: action.method };
            //}
        case "kind":
            if (action.kind === "witness") {
                return { ...state, state: "witness" };
            }
            return { ...state, state: "method", kind: action.kind, kindTitle: action.kindTitle || "", field: action.field || null };
        case "close":
            if (action.reset) {
                // Close and cancel
                return { state: "close", mode: state.mode, method: null, kind: null, kindTitle: "", field: null };
            }

            // Close and return data
            return { ...state, state: "close" };
        default:
            return state;
    }
}

const SignatureDialog: React.FC<ISignatureDialogProps> = function (props) {
    const [state, dispatch] = useReducer((state: ISignatureDialogState, action: SignatureDialogAction) => reducer(state, action, props), {
        state: "kind",
        mode: props.mode,
        method: null,
        kind: null,
        kindTitle: "",
        field: null
    });

    const { isOpen, onRequestClose, mode } = props;
    const wantsClose = state.state === "close";

    useEffect(() => {
        if (!isOpen) {
            dispatch({ type: "reset", mode: state.mode });
        }
    }, [isOpen]);

    useEffect(() => {
        if (wantsClose && state.mode && state.method && state.kind !== "witness") {
            onRequestClose({
                what: state.mode,
                method: state.method,
                kind: state.kind || undefined,
                field: state.field || undefined
            });
        }
    }, [wantsClose, onRequestClose]);

    useEffect(() => {
        dispatch({ type: "reset", mode: mode });
    }, [mode, props.availableSignatures]);

    return props.isOpen
        ? <ReactModal isOpen={isOpen} onRequestClose={() => onRequestClose()}
            overlayClassName="modal-overlay" className="modal-content modal-content-600" portalClassName="modal-portal"
            bodyOpenClassName="modal-open"
            contentLabel="Sign dialog">
            <section className="info-block">
                <header>
                    <h3>Sign Document</h3>
                </header>
                {renderDialogState(props, state, dispatch)}
            </section>
        </ReactModal>
        : null;
}

export default SignatureDialog;
